export type MatcherMode = "legacy" | "grouped"

interface GroupedMatcher {
  matcherGroupId?: number | null
}

export interface IndexedMatcher<T> {
  matcher: T
  /** Position in the source's matcher list, so editors address the row and not its contents. */
  index: number
}

export interface SplitMatcherGroups<T> {
  /** Ungrouped matchers: OR within a field, AND across fields. */
  baseRule: IndexedMatcher<T>[]
  /** Explicit groups by id, ascending: AND within a group, OR across groups. */
  groups: Map<number, IndexedMatcher<T>[]>
}

/** A source is grouped as soon as one matcher carries a group id; the ungrouped rest stays its base rule. */
export function detectMatcherMode(matchers: GroupedMatcher[]): MatcherMode {
  return matchers.some((matcher) => matcher.matcherGroupId != null) ? "grouped" : "legacy"
}

export function splitMatcherGroups<T extends GroupedMatcher>(matchers: T[]): SplitMatcherGroups<T> {
  const baseRule: IndexedMatcher<T>[] = []
  const groups = new Map<number, IndexedMatcher<T>[]>()

  matchers.forEach((matcher, index) => {
    const entry = { matcher, index }
    const groupId = matcher.matcherGroupId
    if (groupId == null) {
      baseRule.push(entry)
      return
    }
    const group = groups.get(groupId)
    if (group) {
      group.push(entry)
    } else {
      groups.set(groupId, [entry])
    }
  })

  return { baseRule, groups: new Map([...groups].sort(([a], [b]) => a - b)) }
}

export function getNextGroupId(matchers: GroupedMatcher[]): number {
  const ids = matchers.map((matcher) => matcher.matcherGroupId).filter((id): id is number => id != null)
  return ids.length === 0 ? 1 : Math.max(...ids) + 1
}
